import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Flag } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

export default function ReportDialog({ open, onOpenChange, reportedUserId, reportedUserName, contextType, contextId }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const token = sessionStorage.getItem('session_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  useEffect(() => {
    if (open && reasons.length === 0) {
      fetch(`${BACKEND_URL}/api/reports/reasons`, { credentials: 'include', headers })
        .then(r => r.json())
        .then(d => setReasons(d.reasons || []))
        .catch(() => {});
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!reason) { toast.error('Please select a reason'); return; }
    if (reason === 'Other' && !details.trim()) { toast.error('Please provide details'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({
          reported_user_id: reportedUserId,
          reason,
          details: details.trim(),
          context_type: contextType || '',
          context_id: contextId || '',
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      toast.success('Report submitted. An admin will review it.');
      setReason('');
      setDetails('');
      onOpenChange(false);
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Flag className="h-4 w-4 text-red-500" /> Report Member
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-1">
          <p className="text-sm text-slate-500">
            Report <span className="font-semibold text-slate-700">{reportedUserName}</span>. This will be reviewed by an admin. All reports are confidential.
          </p>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Reason</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="report-reason-select"><SelectValue placeholder="Select a reason" /></SelectTrigger>
              <SelectContent>
                {reasons.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">
              {reason === 'Other' ? 'Please explain *' : 'Additional details (optional)'}
            </label>
            <Textarea
              data-testid="report-details-input"
              placeholder="Provide any additional context..."
              value={details}
              onChange={e => setDetails(e.target.value)}
              className="min-h-20"
            />
          </div>

          <Button
            data-testid="report-submit-btn"
            onClick={handleSubmit}
            disabled={submitting || !reason}
            className="w-full bg-red-600 hover:bg-red-700 text-white"
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </Button>

          <p className="text-[11px] text-slate-400 text-center">
            Reports are confidential. The reported member will not be notified of who reported them.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
